<template>
  <LoadingDiv v-if="!isReady" />

  <div v-else class="tariff-settings-shell">
    <section class="tariff-form-section">
      <div class="tariff-section-head">
        <div>
          <span>Standard Tariffs</span>
          <h5>Tariff Register</h5>
          <p>Upload and replace the standard tariff spreadsheet attached to each plan.</p>
        </div>
      </div>

      <div class="tariff-summary-grid">
        <div class="tariff-summary-card">
          <PackageCheck :size="20" />
          <span>Plans</span>
          <strong>{{ plans.length }}</strong>
        </div>
        <div class="tariff-summary-card">
          <CircleCheck :size="20" />
          <span>Uploaded</span>
          <strong>{{ uploadedTariffCount }}</strong>
        </div>
        <div class="tariff-summary-card needs-work">
          <Clock3 :size="20" />
          <span>Pending</span>
          <strong>{{ missingTariffCount }}</strong>
        </div>
      </div>

      <div class="tariff-list" v-if="plans.length">
        <article
          v-for="(plan, id) in plans"
          :key="plan.id"
          class="tariff-plan-card"
          :class="{ 'has-error': error && editPlan === plan }"
        >
          <div class="tariff-plan-row">
            <div class="tariff-index">{{ id + 1 }}</div>

            <div class="tariff-plan-main">
              <div class="tariff-plan-title">
                <span class="tariff-plan-icon">
                  <ClipboardCheck :size="18" />
                </span>
                <div>
                  <h6>{{ plan.name }}</h6>
                  <span
                    class="tariff-scheme-pill"
                    :class="{
                      'is-private': schemeType(plan) === 'p',
                      'is-social': schemeType(plan) === 's',
                      'is-missing': !schemeType(plan),
                    }"
                  >
                    {{ schemeLabel(plan) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="tariff-status-card" :class="{ 'is-empty': !scheduleCount(plan) }">
              <span>{{ scheduleCount(plan) ? "Standard Tariff" : "Needs Tariff" }}</span>
              <strong>{{ scheduleCount(plan) ? "Uploaded" : "Pending upload" }}</strong>
            </div>

            <button
              v-if="editPlan !== plan"
              type="button"
              class="tariff-row-action"
              @click="handleEdit(plan)"
            >
              <Upload :size="16" />
              {{ scheduleCount(plan) ? "Replace" : "Upload" }}
            </button>

            <button
              v-else
              type="button"
              class="tariff-row-action is-quiet"
              @click="handleReset"
            >
              <X :size="16" />
              Cancel
            </button>
          </div>

          <form
            v-if="editPlan === plan"
            class="tariff-upload-panel"
            @submit.prevent="handleUpload(plan)"
          >
            <div class="tariff-upload-copy">
              <span class="tariff-eyebrow">Upload Standard Tariff</span>
              <p>Select an Excel file for this plan. The uploaded sheet becomes the active standard tariff.</p>
            </div>

            <div class="tariff-file-field">
              <label class="tariff-file-btn">
                <span>Choose File</span>
                <input type="file" accept=".xlsx,.xls" @change="handleFile">
              </label>
              <span class="tariff-file-name">{{ inputFile || "No file selected" }}</span>
            </div>

            <div class="tariff-upload-status" v-if="uploadProgress && uploadProgress !== '0%'">
              Upload progress: {{ uploadProgress }}
            </div>

            <div class="tariff-error" v-if="fileError || uploadError || error">
              {{ fileError || uploadError || "Select a valid tariff file before uploading." }}
            </div>

            <button class="tariff-submit" :disabled="isSubmitting">
              <span v-if="isSubmitting" class="tariff-button-spinner" aria-hidden="true"></span>
              <Upload v-else :size="16" />
              {{ isSubmitting ? "Uploading..." : "Upload Tariff" }}
            </button>
          </form>
        </article>
      </div>

      <div class="tariff-empty-card" v-else>
        <FileSpreadsheet :size="24" />
        <div>
          <h6>No plans available</h6>
          <p>Create plans first, then return here to attach standard tariff sheets.</p>
        </div>
      </div>
    </section>

    <section class="tariff-note">
      <Info :size="18" />
      <span>
        Standard tariffs are shared across providers unless a provider-specific tariff is configured elsewhere.
      </span>
    </section>

    <div class="tariff-footer-action">
      <button type="button" class="tariff-next-button" @click="handleNext">
        <ArrowRight :size="16" />
        Next
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from "vue";
import { ArrowRight, CircleCheck, ClipboardCheck, Clock3, FileSpreadsheet, Info, PackageCheck, Upload, X } from "@lucide/vue";
import { useStore } from "vuex";
import getItems from "@/composables/getItems";
import useStorage from "@/composables/useStorage";
import LoadingDiv from "@/components/LoadingDiv.vue";
import { adminAPI } from '@/laravel/config';

export default {
  name: "TariffSetupComponent",
  components: { ArrowRight, CircleCheck, ClipboardCheck, Clock3, FileSpreadsheet, Info, LoadingDiv, PackageCheck, Upload, X },
  emits: ["next"],
  setup(props, context) {
    const store = useStore();
    const response = ref(null);
    const file = ref(null);
    const inputFile = ref(null);
    const fileError = ref(null);
    const isReady = ref(false);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const error = ref(null);
    const next = ref(false);
    const editPlan = ref(null);
    const success = ref(null);
    const types = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    // storage
    const { uploadFile, uploadProgress, uploadError, data, completed } =
      useStorage();

    // start
    onMounted(() => {
      // get items
      loadPlans();
    });

    // handle plans loader
    const loadPlans = () => {
      // loasder on
      isReady.value = false;
      // start
      (async () => {
        const { res, err } = await getItems("plan/setup");

        if (res.value) {
          response.value = res.value.data;
        } else {
          error.value = err.value;
        }

        // page is ready
        isReady.value = true;
      })();
    }


    // handle add
    const handleReset = () => {
      // clear files
      file.value = null; //clear file value
      inputFile.value = null; // Clear the file input value
      uploadProgress.value = "0%";
      uploadError.value = null;
      data.value = null;
      completed.value = null;
      // next
      next.value = false;
      // clear error
      error.value = false;
      // clear loading
      isLoading.value = false;
      // clear submitting
      isSubmitting.value = false;
      // clear edit plan
      editPlan.value = null;
    };


    // handle file
    const handleFile = (e) => {
      const selected = e.target.files[0];
      if (selected && types.includes(selected.type)) {
        file.value = selected;
        inputFile.value = file.value.name;
        fileError.value = null;
        error.value = false;
      } else {
        file.value = null;
        inputFile.value = null;
        fileError.value = "Please select an Excel file (.XLSX, XLS)";
        error.value = false;
      }
    };

    // upload
    const handleUpload = (val) => {
      if (file.value) {
        // Start Uploading
        isReady.value = false;
        // others
        isSubmitting.value = true;
        isLoading.value = true;
        uploadError.value = null;

        // Cloud Function
        const fx = "tariffLoader";

        // Start
        let schedule = {
          plan_id: val.id,
          type: "s", // c=customized, s=standard
        };

        // Round up
        const cloudInput = {
          schedule: schedule,
          provider: null,
          newProvider: null,
          endPoint: `${adminAPI}/schedule/tariffs/bulk`,
          authToken: store.state?.user?.token || null,
        };

        // upload
        uploadFile(file.value, fx, cloudInput);

        // rotator will be cleared below 
        // (watchEffect)
      } else {
        // error
        error.value = true;
        // clear rotator
        isReady.value = true;
      }
    };

    // watch upload
    watchEffect(() => {
      if (completed.value) {
        // reset
        handleReset();
        // update plan
        loadPlans(); // also clears rotator
      }
      if (uploadError.value) {
        // error
        error.value = true;
        // clear rotator
        isReady.value = true;
      }
    });

    // handle edit
    const handleEdit = (plan) => {
      // clear
      handleReset();
      // set
      editPlan.value = plan;
    };

    const schemeType = (plan) => {
      return plan && plan.scheme ? plan.scheme.type : null;
    };

    const schemeLabel = (plan) => {
      if (schemeType(plan) === "p") {
        return "Private Scheme";
      }

      if (schemeType(plan) === "s") {
        return "Social Scheme";
      }

      return "Scheme not set";
    };

    const scheduleCount = (plan) => {
      return plan && plan.schedules ? plan.schedules.length : 0;
    };


    // handle submit
    const handleNext = () => {
      // next
      // emit new parameters
      context.emit("next", {
        step: 4,
        isComplete: true,
      });
    };


    // output results (plans)
    const plans = computed(() => {
      if (!response.value) {
        return [];
      } else {
        return response.value;
      }
    });

    const uploadedTariffCount = computed(() => {
      return plans.value.filter((plan) => scheduleCount(plan)).length;
    });

    const missingTariffCount = computed(() => {
      return plans.value.length - uploadedTariffCount.value;
    });


    return {
      isReady,
      isLoading,
      isSubmitting,
      editPlan,
      uploadProgress,
      fileError,
      uploadError,
      file,
      inputFile,
      error,
      handleEdit,
      handleReset,
      handleNext,
      handleUpload,
      handleFile,
      schemeType,
      schemeLabel,
      scheduleCount,
      success,
      next,
      plans,
      uploadedTariffCount,
      missingTariffCount,
    };
  },
};
</script>

<style scoped>
.tariff-settings-shell {
  display: grid;
  gap: 18px;
}

.tariff-form-section,
.tariff-plan-card,
.tariff-empty-card,
.tariff-note {
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
  overflow: hidden;
}

.tariff-section-head {
  background: #fbfcfc;
  border-bottom: 1px solid #edf1f3;
  padding: 22px 24px;
}

.tariff-section-head span,
.tariff-eyebrow {
  color: #00897b;
  display: block;
  font-size: 0.82rem;
  font-weight: 800;
  margin-bottom: 5px;
}

.tariff-section-head h5,
.tariff-empty-card h6 {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.tariff-section-head p,
.tariff-empty-card p,
.tariff-upload-copy p {
  color: #607d8b;
  line-height: 1.5;
  margin: 8px 0 0;
}

.tariff-summary-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, 1fr);
  padding: 18px 20px 6px;
}

.tariff-summary-card {
  align-items: center;
  background: #ffffff;
  border: 1px solid #d3dde1;
  border-radius: 6px;
  display: grid;
  gap: 4px 12px;
  grid-template-columns: 44px 1fr;
  padding: 15px 16px;
}

.tariff-summary-card > svg {
  align-items: center;
  background: #eef7f6;
  border-radius: 6px;
  color: #00897b;
  display: flex;
  grid-row: 1 / span 2;
  height: 44px;
  justify-content: center;
  width: 44px;
}

.tariff-summary-card span {
  color: #607d8b;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
}

.tariff-summary-card strong {
  color: #263238;
  font-size: 1.34rem;
  font-weight: 900;
  line-height: 1;
}

.tariff-summary-card.needs-work > svg {
  background: #fff8e1;
  color: #b26a00;
}

.tariff-summary-card.needs-work strong {
  color: #b26a00;
}

.tariff-list {
  display: grid;
  gap: 12px;
  padding: 14px 20px 20px;
}

.tariff-plan-card {
  background: #fbfcfc;
}

.tariff-plan-card.has-error {
  border-color: #ffcdd2;
}

.tariff-plan-row {
  align-items: center;
  display: grid;
  gap: 16px;
  grid-template-columns: 42px minmax(0, 1fr) minmax(170px, 220px) auto;
  padding: 16px 18px;
}

.tariff-index {
  align-items: center;
  background: #f5f8f9;
  border: 1px solid #dce5e8;
  border-radius: 50%;
  color: #546e7a;
  display: flex;
  font-weight: 800;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.tariff-plan-title {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tariff-plan-icon {
  align-items: center;
  background: #ffffff;
  border: 1px solid #dce5e8;
  border-radius: 6px;
  color: #00897b;
  display: inline-flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}

.tariff-plan-title h6 {
  color: #263238;
  font-size: 1.04rem;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.tariff-scheme-pill,
.tariff-status-card {
  border-radius: 999px;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 800;
  padding: 6px 10px;
}

.tariff-scheme-pill.is-private {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  color: #1565c0;
}

.tariff-scheme-pill.is-social {
  background: #fff8e1;
  border: 1px solid #ffe082;
  color: #8a5a00;
}

.tariff-scheme-pill.is-missing {
  background: #f5f7f8;
  border: 1px solid #dce5e8;
  color: #607d8b;
}

.tariff-status-card {
  align-self: stretch;
  background: #ffffff;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  color: #2e7d32;
  display: grid;
  gap: 3px;
  justify-content: start;
  padding: 11px 13px;
}

.tariff-status-card span {
  color: #607d8b;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
}

.tariff-status-card strong {
  color: #2e7d32;
  font-size: 0.95rem;
  font-weight: 900;
}

.tariff-status-card.is-empty {
  background: #ffffff;
  border-color: #ffe0a3;
}

.tariff-status-card.is-empty strong {
  color: #b26a00;
}

.tariff-row-action {
  align-items: center;
  background: #00897b;
  border: 0;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-weight: 800;
  gap: 7px;
  min-height: 38px;
  padding: 0 13px;
}

.tariff-row-action.is-quiet {
  background: #263238;
}

.tariff-upload-panel {
  background: #ffffff;
  border-top: 1px solid #edf1f3;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 1fr) auto;
  padding: 18px 20px;
}

.tariff-file-field {
  align-items: center;
  border: 1px solid var(--ui-border);
  border-radius: calc(var(--ui-radius) - 2px);
  display: flex;
  gap: .75rem;
  margin: 0;
  padding: .45rem;
}

.tariff-file-btn,
.tariff-submit,
.tariff-next-button {
  align-items: center;
  background: #00897b;
  border: 1px solid #00897b;
  border-radius: 6px;
  color: #fff;
  display: inline-flex;
  gap: .5rem;
  font-weight: 800;
  letter-spacing: 0;
  min-height: 2.5rem;
  padding: 0 .9rem;
  text-transform: none;
}
.tariff-file-btn { cursor: pointer; position: relative; }
.tariff-file-btn input { inset: 0; opacity: 0; position: absolute; width: 100%; }
.tariff-file-name { color: var(--ui-muted-foreground); font-size: .75rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tariff-submit:disabled { cursor: wait; opacity: .6; }
.tariff-button-spinner { animation: tariff-spin .7s linear infinite; border: 2px solid rgb(255 255 255 / .35); border-radius: 50%; border-top-color: #fff; height: 1rem; width: 1rem; }
@keyframes tariff-spin { to { transform: rotate(360deg); } }

.tariff-upload-status {
  align-self: center;
  color: #607d8b;
  font-size: 0.84rem;
  font-weight: 800;
  grid-column: 1 / -1;
}

.tariff-error {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  font-weight: 800;
  grid-column: 1 / -1;
  padding: 12px 14px;
}

.tariff-empty-card {
  align-items: center;
  display: flex;
  gap: 16px;
  margin: 18px 20px 20px;
  padding: 24px;
}

.tariff-empty-card > svg {
  align-items: center;
  background: #eef4f5;
  border-radius: 6px;
  color: #00897b;
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
}

.tariff-footer-action {
  display: flex;
  justify-content: flex-end;
  padding: 6px 0 18px;
}

.tariff-note {
  align-items: center;
  color: #607d8b;
  display: flex;
  gap: 12px;
  padding: 16px 18px;
}

.tariff-note svg {
  color: #00897b;
}

.tariff-note span {
  font-weight: 700;
  line-height: 1.5;
}

@media only screen and (max-width: 992px) {
  .tariff-summary-grid,
  .tariff-plan-row,
  .tariff-upload-panel {
    align-items: flex-start;
    grid-template-columns: 1fr;
  }
}
</style>
